import { Vector2 } from '@babylonjs/core';

export class TouchData {
  //--------------------------------------------------------
  // Getter
  //--------------------------------------------------------
  public firstPoint: Vector2 = Vector2.Zero();
  public point: Vector2 = Vector2.Zero();
  public movement: Vector2 = Vector2.Zero();
  public get direction() {
    return this.point.subtract(this.firstPoint).normalize();
  }
  public get dragVector() {
    return this.point.subtract(this.firstPoint);
  }

  //--------------------------------------------------------
  // Setter
  //--------------------------------------------------------
  private set start(point: Vector2) {
    this.point = point;
  }
  public set first(point: Vector2) {
    this.firstPoint = point;
    this.start = point;
  }
  public set last(point: Vector2) {
    const newPoint = point;
    this.movement = newPoint.subtract(this.point);
    this.start = point;
  }

  //--------------------------------------------------------
  // Reset
  //--------------------------------------------------------
  public reset() {
    this.firstPoint = Vector2.Zero();
    this.point = Vector2.Zero();
    this.movement = Vector2.Zero();
  }

  //--------------------------------------------------------
  // Caclurate Other Touch
  //--------------------------------------------------------
  public getMultiTouchPanPosition(other: TouchData) {
    return this.point.add(other.point).scale(0.5);
  }
  public getPinchSquaredDistance(other: TouchData) {
    return this.getDistance(this.point, other.point);
  }
  public getAngleBetweenTouch(other: TouchData): number {
    return this.getAngleBetweenVectors(this.firstPoint.subtract(other.firstPoint), this.point.subtract(other.point));
  }
  public getSimilarityBetweenTouchDirection(other: TouchData) {
    return this.cosineSimilarity(this.direction, other.direction);
  }
  public getSimilarityBetweenTouchDrag(other: TouchData) {
    return this.cosineSimilarity(this.dragVector, other.dragVector);
  }
  public getSimilarityOtherDirection(other: Vector2) {
    return this.cosineSimilarity(this.direction, other);
  }

  //--------------------------------------------------------
  // Caclurate
  //--------------------------------------------------------
  private cosineSimilarity(vector1: Vector2, vector2: Vector2): number {
    const dotProduct = this.dotProduct(vector1, vector2);
    const normVector1 = Math.sqrt(vector1.x * vector1.x + vector1.y * vector1.y);
    const normVector2 = Math.sqrt(vector2.x * vector2.x + vector2.y * vector2.y);

    if (normVector1 === 0 || normVector2 === 0) {
      return 0; // Prevent division by zero
    }
    const similarity = dotProduct / (normVector1 * normVector2);
    return similarity;
  }
  private getAngleBetweenVectors(referenceVector: Vector2, targetVector: Vector2) {
    const dot = this.dotProduct(referenceVector, targetVector);
    const magReference = this.magnitude(referenceVector);
    const magTarget = this.magnitude(targetVector);

    if (magReference === 0 || magTarget === 0) {
      return 0;
    }

    const cosTheta = dot / (magReference * magTarget);
    const radianAngle = Math.acos(Math.max(-1, Math.min(1, cosTheta)));

    const cross = this.crossProduct(referenceVector, targetVector);

    // Determine the sign (clockwise or counterclockwise)
    const sign = cross >= 0 ? 1 : -1;

    return radianAngle * (180 / Math.PI) * sign;
  }
  private crossProduct(v1: Vector2, v2: Vector2) {
    return v1.x * v2.y - v1.y * v2.x;
  }
  private getDistance(a: Vector2, b: Vector2) {
    const distX = a.x - b.x;
    const distY = a.y - b.y;

    return distX * distX + distY * distY;
  }
  private dotProduct(a: Vector2, b: Vector2): number {
    return a.x * b.x + a.y * b.y;
  }
  private magnitude(vector: Vector2): number {
    return Math.sqrt(vector.x ** 2 + vector.y ** 2);
  }
}
